import { createFileRoute, Link } from "@tanstack/react-router";
import { SessionAwareLink } from "@/components/session-aware-link";
import { SiteFooter } from "@/components/site-footer";
import { OFFICIAL_APP_URL } from "@/lib/app-url";
import { formatDateBR } from "@/lib/datetime";

export const Route = createFileRoute("/termos")({
  head: () => ({
    meta: [
      { title: "Termos de Uso · Beit Tefilah Rechovot" },
      {
        name: "description",
        content:
          "Termos de Uso da área de membros da Beit Tefilah Rechovot: regras de acesso, conteúdo e conduta.",
      },
      { property: "og:title", content: "Termos de Uso · Beit Tefilah Rechovot" },
      {
        property: "og:description",
        content: "Condições para uso da plataforma de ensinos da Beit Tefilah Rechovot.",
      },
      { property: "og:url", content: `${OFFICIAL_APP_URL}/termos` },
    ],
    links: [{ rel: "canonical", href: `${OFFICIAL_APP_URL}/termos` }],
  }),
  component: TermosPage,
});

function TermosPage() {
  return (
    <div className="flex min-h-screen flex-col bg-background">
      <header className="border-b border-border/60 bg-background/95">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-4">
          <Link to="/" className="font-serif text-lg font-semibold text-primary">
            Beit Tefilah Rechovot
          </Link>
          <div className="flex gap-4 text-sm">
            <Link to="/privacidade" className="text-muted-foreground hover:text-primary">
              Privacidade
            </Link>
            <SessionAwareLink className="text-muted-foreground hover:text-primary" />
          </div>
        </div>
      </header>
      <main className="mx-auto w-full max-w-3xl flex-1 px-6 py-12">
        <h1 className="font-serif text-4xl font-semibold text-primary">Termos de Uso</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Última atualização: {formatDateBR(new Date())}
        </p>

        <div className="prose prose-slate mt-8 max-w-none space-y-6 text-foreground/90">
          <section>
            <h2 className="font-serif text-2xl font-semibold text-primary">1. Aceitação</h2>
            <p>
              Ao criar uma conta e utilizar a plataforma de ensinos da{" "}
              <strong>Beit Tefilah Rechovot</strong>, você concorda com estes Termos de Uso e com a{" "}
              <Link to="/privacidade" className="text-primary underline">
                Política de Privacidade
              </Link>
              .
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl font-semibold text-primary">
              2. Elegibilidade e aprovação
            </h2>
            <p>
              O acesso é destinado exclusivamente a membros da congregação. Todo novo cadastro passa
              por aprovação manual de um administrador. Cadastros com informações falsas poderão ser
              recusados ou removidos.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl font-semibold text-primary">3. Conta e segurança</h2>
            <ul className="list-inside list-disc space-y-1">
              <li>
                Você é responsável por manter sua senha em sigilo e por toda atividade em sua conta.
              </li>
              <li>Informe imediatamente ao suporte qualquer uso não autorizado.</li>
              <li>É proibido compartilhar credenciais de acesso.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-serif text-2xl font-semibold text-primary">4. Conteúdo</h2>
            <p>
              Os ensinos são de propriedade da Beit Tefilah Rechovot e/ou dos respectivos
              ministradores, veiculados por meio do canal oficial no YouTube. É{" "}
              <strong>proibida</strong> a reprodução, redistribuição, edição ou comercialização sem
              autorização prévia por escrito.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl font-semibold text-primary">
              5. Comentários e conduta
            </h2>
            <p>
              Ao publicar comentários, você concorda em manter respeito, edificação e alinhamento
              com os princípios da congregação. É proibido:
            </p>
            <ul className="list-inside list-disc space-y-1">
              <li>Discurso de ódio, ofensas, ameaças ou assédio;</li>
              <li>Divulgação de conteúdo ilegal, obsceno ou spam;</li>
              <li>Publicação de dados pessoais de terceiros sem consentimento.</li>
            </ul>
            <p>
              A plataforma pode moderar, editar ou remover comentários e suspender contas que violem
              estas regras.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl font-semibold text-primary">6. Disponibilidade</h2>
            <p>
              Empenhamo-nos para manter a plataforma disponível, mas ela pode ficar indisponível
              para manutenção ou por falhas de terceiros (ex.: YouTube). Não garantimos
              disponibilidade ininterrupta.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl font-semibold text-primary">7. Encerramento</h2>
            <p>
              Você pode solicitar a exclusão da sua conta a qualquer momento por{" "}
              <a href="mailto:contato@rechovot.org.br" className="text-primary underline">
                contato@rechovot.org.br
              </a>
              . A congregação pode encerrar contas que descumpram estes termos.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl font-semibold text-primary">8. Alterações</h2>
            <p>
              Podemos atualizar estes Termos periodicamente. A versão vigente estará sempre
              publicada nesta página.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl font-semibold text-primary">9. Contato</h2>
            <p>
              Dúvidas sobre estes termos:{" "}
              <a href="mailto:contato@rechovot.org.br" className="text-primary underline">
                contato@rechovot.org.br
              </a>
              .
            </p>
          </section>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
